import type { AgentRuntimeSnapshot, AgentType } from "@cord/shared"
import {
  stripClaudeDerivedProjectionForPersistence,
  type ClaudeRawProjection,
} from "@/lib/claude-raw-runtime"
import type { CursorRawProjection } from "@/lib/cursor-raw-runtime"
import type { OpenCodeRawProjection } from "@/lib/opencode-raw-runtime"
import {
  SESSION_RUNTIME_ADAPTERS,
} from "@/lib/session-runtime-adapters"
import { reconcileRuntimeSnapshot } from "@/lib/runtime-snapshot-stability"
import type { ThreadSyncResumeState } from "@/lib/thread-sync-resume-state"

export type ManagedRuntimeState = {
  agentType: AgentType | null
  snapshot: AgentRuntimeSnapshot | null
  lastAppliedTimestamp: number | null
  lastEventId: number | null
  rawOpenCodeProjection: OpenCodeRawProjection | null
  rawClaudeProjection: ClaudeRawProjection | null
  rawCursorProjection: CursorRawProjection | null
}

export type ManagedRuntimeRawProjectionState = Pick<
  ManagedRuntimeState,
  "rawOpenCodeProjection" | "rawClaudeProjection" | "rawCursorProjection"
>

function normalizeNonNegativeInteger(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    return null
  }

  return Math.floor(value)
}

export function buildEmptyManagedRuntimeRawProjectionState(): ManagedRuntimeRawProjectionState {
  return {
    rawOpenCodeProjection: null,
    rawClaudeProjection: null,
    rawCursorProjection: null,
  }
}

export function buildEmptyManagedRuntimeState(): ManagedRuntimeState {
  return {
    agentType: null,
    snapshot: null,
    lastAppliedTimestamp: null,
    lastEventId: null,
    ...buildEmptyManagedRuntimeRawProjectionState(),
  }
}

export function resolveManagedRuntimeRawProjectionState(
  runtimeState: ManagedRuntimeState,
): ManagedRuntimeRawProjectionState {
  return {
    rawOpenCodeProjection: runtimeState.rawOpenCodeProjection,
    rawClaudeProjection: runtimeState.rawClaudeProjection,
    rawCursorProjection: runtimeState.rawCursorProjection,
  }
}

export function stripManagedRuntimeStateForCheckpointPersistence(
  runtimeState: ManagedRuntimeState,
): ManagedRuntimeState {
  if (runtimeState.rawClaudeProjection === null) return runtimeState

  const rawClaudeProjection = stripClaudeDerivedProjectionForPersistence(
    runtimeState.rawClaudeProjection,
  )
  if (rawClaudeProjection === runtimeState.rawClaudeProjection) return runtimeState

  return {
    ...runtimeState,
    rawClaudeProjection,
  }
}

export function hasAnyManagedRuntimeRawProjection(
  rawProjectionState: ManagedRuntimeRawProjectionState,
): boolean {
  return (
    rawProjectionState.rawOpenCodeProjection !== null ||
    rawProjectionState.rawClaudeProjection !== null ||
    rawProjectionState.rawCursorProjection !== null
  )
}

export function deriveExpectedRuntimeCursor(args: {
  runtimeState: ManagedRuntimeState | null
  resume: Pick<ThreadSyncResumeState, "cursor">
}): number | null {
  const snapshotCursor = args.runtimeState?.snapshot?.cursorState.providerEventId
  if (
    typeof snapshotCursor === "number" &&
    Number.isFinite(snapshotCursor) &&
    snapshotCursor > 0
  ) {
    return Math.floor(snapshotCursor)
  }

  if (
    typeof args.runtimeState?.lastEventId === "number" &&
    Number.isFinite(args.runtimeState.lastEventId) &&
    args.runtimeState.lastEventId > 0
  ) {
    return Math.floor(args.runtimeState.lastEventId)
  }

  if (
    typeof args.resume.cursor === "number" &&
    Number.isFinite(args.resume.cursor) &&
    args.resume.cursor > 0
  ) {
    return Math.floor(args.resume.cursor)
  }

  return 0
}

export function hasConsistentContinuityValidatedCursor(args: {
  runtimeState: ManagedRuntimeState
  resume: Pick<ThreadSyncResumeState, "cursor">
}): boolean {
  const resumeCursor = normalizeNonNegativeInteger(args.resume.cursor)
  const snapshotCursor = normalizeNonNegativeInteger(
    args.runtimeState.snapshot?.cursorState.providerEventId,
  )
  const lastEventId = normalizeNonNegativeInteger(args.runtimeState.lastEventId)

  return (
    resumeCursor !== null &&
    snapshotCursor !== null &&
    lastEventId !== null &&
    resumeCursor === snapshotCursor &&
    resumeCursor === lastEventId
  )
}

export function hasSerializableContinuityValidatedReplicaProjection(
  runtimeState: ManagedRuntimeState,
): boolean {
  switch (runtimeState.agentType) {
    case "opencode": {
      const rawProjection = runtimeState.rawOpenCodeProjection
      return !!rawProjection && SESSION_RUNTIME_ADAPTERS.opencode.isRawProjectionLike(rawProjection)
    }
    case "claude_code": {
      const rawProjection = runtimeState.rawClaudeProjection
      return !!rawProjection && SESSION_RUNTIME_ADAPTERS.claude_code.isRawProjectionLike(rawProjection)
    }
    case "cursor": {
      const rawProjection = runtimeState.rawCursorProjection
      return !!rawProjection && SESSION_RUNTIME_ADAPTERS.cursor.isRawProjectionLike(rawProjection)
    }
    default:
      return false
  }
}

function normalizeManagedOpenCodeRuntimeSnapshot(args: {
  cursor?: number | null
  runtimeState: ManagedRuntimeState
}): {
  committedCursor: number
  snapshot: AgentRuntimeSnapshot | null
} {
  if (args.runtimeState.rawOpenCodeProjection === null) {
    return {
      committedCursor: normalizeNonNegativeInteger(args.cursor) ??
        normalizeNonNegativeInteger(args.runtimeState.lastEventId) ??
        normalizeNonNegativeInteger(args.runtimeState.snapshot?.cursorState.providerEventId) ??
        0,
      snapshot: args.runtimeState.snapshot,
    }
  }

  const previousSnapshot = args.runtimeState.snapshot
  const explicitCursor = normalizeNonNegativeInteger(args.cursor)
  const snapshotCursor = normalizeNonNegativeInteger(
    previousSnapshot?.cursorState.providerEventId,
  )
  const lastEventId = normalizeNonNegativeInteger(args.runtimeState.lastEventId)
  const committedCursor = explicitCursor ?? Math.max(snapshotCursor ?? 0, lastEventId ?? 0)
  const rebuiltSnapshot = SESSION_RUNTIME_ADAPTERS.opencode.buildSnapshot(
    args.runtimeState.rawOpenCodeProjection,
    committedCursor,
  )
  const preservedConversationEpoch =
    typeof previousSnapshot?.conversationEpoch === "number" &&
      Number.isFinite(previousSnapshot.conversationEpoch)
      ? Math.max(0, Math.floor(previousSnapshot.conversationEpoch))
      : rebuiltSnapshot.conversationEpoch
  const normalizedSnapshot: AgentRuntimeSnapshot = {
    ...rebuiltSnapshot,
    pendingMessages: previousSnapshot?.pendingMessages ?? rebuiltSnapshot.pendingMessages,
    activeEditOperation: previousSnapshot?.activeEditOperation ?? rebuiltSnapshot.activeEditOperation,
    conversationEpoch: preservedConversationEpoch,
    sessionActionOutcome: previousSnapshot?.sessionActionOutcome ?? rebuiltSnapshot.sessionActionOutcome,
    capabilities: previousSnapshot?.capabilities ?? rebuiltSnapshot.capabilities,
  }

  return {
    committedCursor,
    snapshot: reconcileRuntimeSnapshot(previousSnapshot, normalizedSnapshot),
  }
}

export function normalizeManagedRuntimeState(args: {
  cursor?: number | null
  runtimeState: ManagedRuntimeState
}): ManagedRuntimeState {
  if (args.runtimeState.agentType !== "opencode") {
    return args.runtimeState
  }

  const normalizedState = normalizeManagedOpenCodeRuntimeSnapshot(args)
  const explicitCursor = normalizeNonNegativeInteger(args.cursor)
  const normalizedLastEventId = explicitCursor ?? Math.max(
    normalizeNonNegativeInteger(args.runtimeState.lastEventId) ?? 0,
    normalizedState.committedCursor,
  )
  if (
    normalizedState.snapshot === args.runtimeState.snapshot &&
    normalizedLastEventId === (normalizeNonNegativeInteger(args.runtimeState.lastEventId) ?? 0)
  ) {
    return args.runtimeState
  }

  return {
    ...args.runtimeState,
    snapshot: normalizedState.snapshot,
    lastEventId: normalizedLastEventId,
  }
}
